/**
 * Personal todos — read model for `/personal-todos`.
 *
 * Type re-exports keyed off the `personal_todos` table, plus URL-state codec
 * for filter/sort params per `web-mock/features/personal-todos/` (2026-05-17).
 *
 * URL keys: `status`, `priority`, `due`, `sort`, `include_archived`.
 * Default state round-trips to an empty query string.
 * Legacy `?show=all` maps to `status=all` when `status` is absent.
 */
import type { Database } from '../../types/database';

export type PersonalTodoRow = Database['public']['Tables']['personal_todos']['Row'];
export type PersonalTodoInsert = Database['public']['Tables']['personal_todos']['Insert'];
export type PersonalTodoUpdate = Database['public']['Tables']['personal_todos']['Update'];

export type PersonalTodoPriority = Database['public']['Enums']['personal_todo_priority'];
export type PersonalTodoStatus = Database['public']['Enums']['personal_todo_status'];

/** @deprecated Use PersonalTodosStatusFilter — kept for one-release legacy reads */
export type PersonalTodosShow = 'open' | 'all';

export type PersonalTodosStatusFilter = 'open' | 'done' | 'all';
export type PersonalTodosPriorityFilter = 'all' | PersonalTodoPriority;
export type PersonalTodosDueFilter =
  | 'all'
  | 'overdue'
  | 'today'
  | 'week'
  | 'none';
export type PersonalTodosSort =
  | 'manual'
  | 'created_desc'
  | 'created_asc'
  | 'priority_desc'
  | 'priority_asc';

export type PersonalTodosSection = 'open' | 'done' | 'archived';

export interface PersonalTodosListState {
  status: PersonalTodosStatusFilter;
  priority: PersonalTodosPriorityFilter;
  due: PersonalTodosDueFilter;
  sort: PersonalTodosSort;
  includeArchived: boolean;
}

export const PERSONAL_TODOS_DEFAULT_STATE: PersonalTodosListState = Object.freeze({
  status: 'open',
  priority: 'all',
  due: 'all',
  sort: 'manual',
  includeArchived: false
});

const PRIORITY_RANK: Record<PersonalTodoPriority, number> = {
  high: 0,
  medium: 1,
  low: 2
};

const VALID_STATUS = new Set<PersonalTodosStatusFilter>(['open', 'done', 'all']);
const VALID_PRIORITY = new Set<PersonalTodosPriorityFilter>([
  'all',
  'high',
  'medium',
  'low'
]);
const VALID_DUE = new Set<PersonalTodosDueFilter>([
  'all',
  'overdue',
  'today',
  'week',
  'none'
]);
const VALID_SORT = new Set<PersonalTodosSort>([
  'manual',
  'created_desc',
  'created_asc',
  'priority_desc',
  'priority_asc'
]);

function readStatus(params: URLSearchParams): PersonalTodosStatusFilter {
  const raw = params.get('status');
  if (raw && VALID_STATUS.has(raw as PersonalTodosStatusFilter)) {
    return raw as PersonalTodosStatusFilter;
  }
  // Legacy `show=all` → `status=all`
  if (params.get('show') === 'all') return 'all';
  return 'open';
}

function readPriority(params: URLSearchParams): PersonalTodosPriorityFilter {
  const raw = params.get('priority');
  if (raw && VALID_PRIORITY.has(raw as PersonalTodosPriorityFilter)) {
    return raw as PersonalTodosPriorityFilter;
  }
  return 'all';
}

function readDue(params: URLSearchParams): PersonalTodosDueFilter {
  const raw = params.get('due');
  if (raw && VALID_DUE.has(raw as PersonalTodosDueFilter)) {
    return raw as PersonalTodosDueFilter;
  }
  return 'all';
}

function readSort(params: URLSearchParams): PersonalTodosSort {
  const raw = params.get('sort');
  if (raw && VALID_SORT.has(raw as PersonalTodosSort)) {
    return raw as PersonalTodosSort;
  }
  return 'manual';
}

function readIncludeArchived(params: URLSearchParams): boolean {
  const raw = params.get('include_archived');
  return raw === '1' || raw === 'true';
}

export function parsePersonalTodosSearchParams(
  params: URLSearchParams
): PersonalTodosListState {
  return {
    status: readStatus(params),
    priority: readPriority(params),
    due: readDue(params),
    sort: readSort(params),
    includeArchived: readIncludeArchived(params)
  };
}

export function personalTodosStateToSearchParams(
  state: PersonalTodosListState
): URLSearchParams {
  const p = new URLSearchParams();
  if (state.status !== 'open') p.set('status', state.status);
  if (state.priority !== 'all') p.set('priority', state.priority);
  if (state.due !== 'all') p.set('due', state.due);
  if (state.sort !== 'manual') p.set('sort', state.sort);
  if (state.includeArchived) p.set('include_archived', '1');
  return p;
}

export function filterBadgeCount(state: PersonalTodosListState): number {
  let n = 0;
  if (state.status !== 'open') n++;
  if (state.priority !== 'all') n++;
  if (state.due !== 'all') n++;
  if (state.includeArchived) n++;
  return n;
}

export function comparePersonalTodoPriority(
  a: PersonalTodoPriority,
  b: PersonalTodoPriority
): number {
  return PRIORITY_RANK[a] - PRIORITY_RANK[b];
}

function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function matchesPriorityFilter(
  row: PersonalTodoRow,
  filter: PersonalTodosPriorityFilter
): boolean {
  if (filter === 'all') return true;
  return row.priority === filter;
}

export function matchesDueFilter(
  row: PersonalTodoRow,
  filter: PersonalTodosDueFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'none') return row.due_date == null;
  if (!row.due_date) return false;

  const todayMs = startOfTodayMs();
  const dueMs = Date.parse(row.due_date);
  if (!Number.isFinite(dueMs)) return false;
  const diffDays = Math.round((dueMs - todayMs) / 86_400_000);

  if (filter === 'overdue') return diffDays < 0;
  if (filter === 'today') return diffDays === 0;
  if (filter === 'week') return diffDays >= 0 && diffDays <= 7;
  return true;
}

export function matchesRowFilters(
  row: PersonalTodoRow,
  state: Pick<PersonalTodosListState, 'priority' | 'due'>
): boolean {
  return (
    matchesPriorityFilter(row, state.priority) &&
    matchesDueFilter(row, state.due)
  );
}

function sortManualOpen(
  rows: ReadonlyArray<PersonalTodoRow>
): ReadonlyArray<PersonalTodoRow> {
  return rows.slice().sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    const byPrio = comparePersonalTodoPriority(a.priority, b.priority);
    if (byPrio !== 0) return byPrio;
    return b.created_at.localeCompare(a.created_at);
  });
}

function sortByCreated(
  rows: ReadonlyArray<PersonalTodoRow>,
  asc: boolean
): ReadonlyArray<PersonalTodoRow> {
  return rows.slice().sort((a, b) => {
    const cmp = a.created_at.localeCompare(b.created_at);
    return asc ? cmp : -cmp;
  });
}

function sortByPriority(
  rows: ReadonlyArray<PersonalTodoRow>,
  asc: boolean
): ReadonlyArray<PersonalTodoRow> {
  return rows.slice().sort((a, b) => {
    const cmp = comparePersonalTodoPriority(a.priority, b.priority);
    return asc ? -cmp : cmp;
  });
}

function sortDoneDefault(
  rows: ReadonlyArray<PersonalTodoRow>
): ReadonlyArray<PersonalTodoRow> {
  return rows.slice().sort((a, b) => {
    const aTs = a.done_at ?? '';
    const bTs = b.done_at ?? '';
    return bTs.localeCompare(aTs);
  });
}

function sortArchivedDefault(
  rows: ReadonlyArray<PersonalTodoRow>
): ReadonlyArray<PersonalTodoRow> {
  return rows.slice().sort((a, b) => {
    const aTs = a.archived_at ?? '';
    const bTs = b.archived_at ?? '';
    return bTs.localeCompare(aTs);
  });
}

export function sortPersonalTodos(
  rows: ReadonlyArray<PersonalTodoRow>,
  sort: PersonalTodosSort,
  section: PersonalTodosSection
): ReadonlyArray<PersonalTodoRow> {
  if (sort === 'manual' && section === 'open') return sortManualOpen(rows);
  if (sort === 'created_desc') return sortByCreated(rows, false);
  if (sort === 'created_asc') return sortByCreated(rows, true);
  if (sort === 'priority_desc') return sortByPriority(rows, false);
  if (sort === 'priority_asc') return sortByPriority(rows, true);
  if (section === 'done') return sortDoneDefault(rows);
  if (section === 'archived') return sortArchivedDefault(rows);
  return rows.slice();
}

export function formatRelativeDue(due: string | null): string {
  if (!due) return '—';
  const todayMs = startOfTodayMs();
  const dueMs = Date.parse(due);
  if (!Number.isFinite(dueMs)) return '—';
  const diff = Math.round((dueMs - todayMs) / 86_400_000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff <= 7) return `in ${diff}d`;
  return new Date(due).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

export const PERSONAL_TODOS_BODY_SOFT_CAP_BYTES = 12 * 1024;
export const PERSONAL_TODOS_BODY_HARD_CAP_BYTES = 16 * 1024;

export function bodyByteLength(body: string): number {
  return new Blob([body]).size;
}

/** Done rows older than this window fall out of the `Recently done` section. */
export const PERSONAL_TODOS_RECENT_DONE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
